import { Dialog, Button, Snackbar, SnackbarCloseReason, Alert } from '@mui/material';
import axios from 'axios';
import baseURL from '../../../configs/api';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../components/hooks/useTheme';
import useDelete from '../../../components/hooks/useDelete';
import colorConfigs from '../../../configs/colorConfigs';
import useAuth from '../../../components/hooks/useAuth';
import Cookies from 'js-cookie';
import useTenant from '../../../components/hooks/useTenant';

type Props = {
  particularQuotes: any;
  showPopup: boolean;
  hidePopup: () => void;
  deleteQuote: (id: number) => void;
}

export const QuotesDetailsPopup = ({ particularQuotes, showPopup, hidePopup, deleteQuote }: Props) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { theme, auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const { themeStyles } = useTheme();
  const { setDeletePopup, setData: setDeleteData }: any = useDelete();
  const navigate = useNavigate();
  const [showCopyPublicLink, setShowCopuPublicLink] = useState<boolean>(false);
  const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

  const handleClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setShowCopuPublicLink(false);
  };

  const copyPublicLink = () => {
    const text = `Reservation Type

    ${particularQuotes?.reservationType}
    
    Tags
    
    ${particularQuotes?.tag}
    
    Customer Name
    
    ${particularQuotes?.customerName}
    
    Customer Email
    
    ${particularQuotes?.customerEmail}
    
    Pickup Date
    
    ${particularQuotes?.pickupDate}
    
    Return Date
    
    ${particularQuotes?.returnDate}
    
    Pickup Location
    
    ${particularQuotes?.pickupLocation}
    
    Return Location
    
    ${particularQuotes?.returnLocation}
    
    Branch
    
    ${particularQuotes?.branchName}
    
    Vehicle Class
    
    ${particularQuotes?.vehicleClassName}
    
    Forced Security Deposit

    ${particularQuotes?.securityDepositAmount}
    
    Manual Discount
    
     ${particularQuotes?.discount}
    
    Phone Number
    
    ${particularQuotes?.phoneNumber}
    
    Rack Price
    
    ${particularQuotes?.rackPrice}
    
    Total Price
    
    ${particularQuotes?.totalPrice}`;

    navigator.clipboard.writeText(text);

    setShowCopuPublicLink(true);
  }

  const handleDelete = () => {
    setDeleteData({
      id: particularQuotes.id,
      tilte: `Delete`,
      summary: `Are you sure you want to delete this Quote?`,
      isDelete: (callback: any) => {
        axios
          .delete(`${baseURL}quote?id=${particularQuotes.id}&userId=${auth?.id}`,
            {
              headers: {
                'X-Tenant-ID': tenantId?.toString(),
                'Content-Type': 'application/json'
              }
            }
          )
          .then(() => {
            deleteQuote(particularQuotes.id);
            hidePopup();
            handleTenantError(null);
          })
          .catch((error) => {
            handleTenantError(error);
          });

        callback();
      }
    });

    setDeletePopup(true);
  };

  const createNewReservation = () => {
    navigate(`/carrental/reservations/addreservation?quoteId=${particularQuotes?.id}`);
  }

  const goToReservation = (reservationId: number) => {
    navigate(`/carrental/reservations/editreservation?id=${reservationId}`);
  }


  return (
    <>
      <Dialog
        open={showPopup}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '800px',
            width: '100%',
            borderRadius: '16px',
          },
        }}
      >
        <div className={`px-[24px] py-[10px] h-full ${themeStyles.main.tailwindSectionBg}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320.591 320.591"
            className={`w-3 cursor-pointer float-right ${closeIconFill}`}
            onClick={() => hidePopup()}
          >
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
          </svg>
          <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
            <div className="w-full mt-2">
              <div className={`w-full`}>
                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                  <div className="flex items-center gap-1.5">
                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Quotes Details - {particularQuotes?.id}</h2>
                  </div>
                </div>

                <div className="mt-2 p-2">

                  <div className="w-full">
                    <div className="grid grid-cols-3 gap-6">
                      {/* First Column */}
                      <div className="space-y-4">
                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Reservation Type</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.reservationType}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Customer Name</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.customerName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Pickup Date</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.pickupDate}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Pickup Location</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.customerName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Branch</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.branchName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Forced Security Deposit</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.securityDepositAmount}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Manual Discount</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.discount}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Created At</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.createdDate}</span>
                        </div>
                      </div>

                      {/* Second Column */}
                      <div className="space-y-4">
                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Rack Price</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.rackPrice}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Tags</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.tag}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Customer Email</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.customerEmail}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Return Date</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.returnDate}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Return Location</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.returenLoationName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Vehicle Class</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.vehicleClassName}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Phone Number</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.phoneNumber}</span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                          <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Total Price</span>
                          <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularQuotes?.totalPrice}</span>
                        </div>
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 mt-6">
                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.cancel.bg,
                          border: colorConfigs.btn.cancel.border,
                          color: colorConfigs.btn.cancel.color
                        }}
                        onClick={() => hidePopup()}
                      >
                        Close
                      </Button>

                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.delete.bg
                        }}
                        onClick={() => handleDelete()}
                      >
                        Delete
                      </Button>

                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.main.bg
                        }}
                        onClick={() => copyPublicLink()}
                      >
                        Copy Public Link
                      </Button>

                      <Button variant="contained"
                        sx={{
                          backgroundColor: colorConfigs.btn.main.bg
                        }}
                        onClick={() => {
                          if (particularQuotes?.reservationId && Number(particularQuotes?.reservationId) > 0) {
                            goToReservation(particularQuotes?.reservationId);
                          }
                          else {
                            createNewReservation()
                          }
                        }}
                      >
                        {particularQuotes?.reservationId && Number(particularQuotes?.reservationId) > 0 ? "Go to Reservation" : "Create Reservation"}
                      </Button>
                    </div>

                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showCopyPublicLink}
        autoHideDuration={1000}
        onClose={handleClose}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Data Copy to Clipboard.
        </Alert>
      </Snackbar>
    </>
  )
}